import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../Context/TranslationContext';
import { useCartStore } from '../Zustand/cartStore';
import { toast, ToastContainer } from 'react-toastify';
import { useAuthStore } from '../Zustand/authStore';
import api from '../apiConfig/api';
import 'react-toastify/dist/ReactToastify.css';

export default function LoginSignupPage() {
  const navigate = useNavigate();
  const { isTamil } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);
  const { setAuth, setPendingCartProduct, pendingCartProduct } = useAuthStore();
  const { addToCart } = useCartStore();

 
  const translations = {
    createAccount: {
      en: 'Create an Account',
      ta: 'கணக்கை உருவாக்கவும்'
    },
    welcomeBack: {
      en: 'Welcome Back',
      ta: 'மீண்டும் வரவேற்கிறோம்'
    },
    joinCommunity: {
      en: 'Join our community today!',
      ta: 'இன்றே எங்கள் சமூகத்தில் இணையுங்கள்!'
    },
    signInPrompt: {
      en: 'Please sign in to your account',
      ta: 'உங்கள் கணக்கில் உள்நுழையவும்'
    },
    username: {
      en: 'Username',
      ta: 'பயனர்பெயர்'
    },
    enterUsername: {
      en: 'Enter your username',
      ta: 'உங்கள் பயனர்பெயரை உள்ளிடவும்'
    },
    emailAddress: {
      en: 'Email address',
      ta: 'மின்னஞ்சல் முகவரி'
    },
    enterEmail: {
      en: 'Enter your email address',
      ta: 'உங்கள் மின்னஞ்சல் முகவரியை உள்ளிடவும்'
    },
    password: {
      en: 'Password',
      ta: 'கடவுச்சொல்'
    },
    enterPassword: {
      en: 'Enter your password',
      ta: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்'
    },
    loginAs: {
      en: 'Login As',
      ta: 'இவராக உள்நுழையவும்'
    },
    user: {
      en: 'User',
      ta: 'பயனர்'
    },
    admin: {
      en: 'Admin',
      ta: 'நிர்வாகி'
    },
    rememberMe: {
      en: 'Remember me',
      ta: 'என்னை நினைவில் வைத்துக்கொள்'
    },
    forgotPassword: {
      en: 'Forgot your password?',
      ta: 'கடவுச்சொல் மறந்துவிட்டதா?'
    },
    processing: {
      en: 'Processing...',
      ta: 'செயலாக்கப்படுகிறது...'
    },
    signUp: {
      en: 'Sign Up',
      ta: 'பதிவு செய்யவும்'
    },
    signIn: {
      en: 'Sign In',
      ta: 'உள்நுழையவும்'
    },
    alreadyHaveAccount: {
      en: 'Already have an account?',
      ta: 'ஏற்கனவே ஒரு கணக்கு உள்ளதா?'
    },
    dontHaveAccount: {
      en: "Don't have an account?",
      ta: 'கணக்கு இல்லையா?'
    }
  };

  useEffect(() => {
    if (autoLogin) {
      handleSubmit(null, true);
      setAutoLogin(false);
    }
  }, [autoLogin]);

  const validateForm = () => {
    if (isSignup && !username.trim()) {
      setError(isTamil ? 'பயனர்பெயர் தேவை' : 'Username is required');
      return false;
    }
    if (!email.trim()) {
      setError(isTamil ? 'மின்னஞ்சல் தேவை' : 'Email is required');
      return false;
    }
    if (!password.trim()) {
      setError(isTamil ? 'கடவுச்சொல் தேவை' : 'Password is required');
      return false;
    }
    if (password.length < 8) {
      setError(isTamil ? 'கடவுச்சொல் குறைந்தது 8 எழுத்துகள் நீளமாக இருக்க வேண்டும்' : 'Password must be at least 8 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, isAutoLogin = false) => {
    if (e && !isAutoLogin) e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;

    setIsLoading(true);

    try {
      if (isSignup) {
        const signupResponse = await api.post('register', {
          email,
          password,
          username,
          loginAs: 'user' // Fixed default value
        });
      
        if (signupResponse.data && signupResponse.data.message === "Login registered successfully") { // Fixed success check
          // Show success toast
          toast.success(isTamil ? 'பதிவு வெற்றிகரமாக முடிந்தது!' : 'Registration successful!');
          
          // Store credentials for login
          const registeredEmail = email;
          const registeredPassword = password;
          
          // Clear form and switch immediately
          setUsername('');
          setEmail('');
          setPassword('');
          setIsSignup(false);
          
          // After a brief delay, set the login credentials
          setTimeout(() => {
            setEmail(registeredEmail);
            setPassword(registeredPassword);
          }, 100);
        }
      } else {
        const response = await api.post('login', { email, password });
        
        if (!response.data?.output?.token || !response.data?.output?.data) {
          throw new Error('Incomplete login response data');
        }

        const token = response.data.output.token;
        const userData = response.data.output.data;

        localStorage.clear();
        setAuth(token, userData.id, userData.username);
        localStorage.setItem('authToken', token);
        localStorage.setItem('userId', userData.id);

        if (rememberMe) {
          localStorage.setItem('rememberEmail', email);
        }

        toast.success(isTamil ? 'உள்நுழைவு வெற்றிகரமானது!' : 'Login successful!');

        if (pendingCartProduct) {
          try {
            const cartResponse = await api.post('addCart', {
              productId: pendingCartProduct.productId,
              loginId: userData.id,
              quantity: pendingCartProduct.quantity
            });

            if (cartResponse.data.success) {
              addToCart(pendingCartProduct);
              setPendingCartProduct(null);
            }
          } catch (cartError) {
            console.error('Cart Addition Error:', cartError);
            toast.error(isTamil ? 'கார்ட் சேர்க்கும் போது பிழை ஏற்பட்டது' : 'Error adding item to cart');
          }
        }

        setTimeout(() => {
          navigate(pendingCartProduct ? '/cart' : '/');
        }, 1000);
      }
    } catch (error) {
      console.error('Authentication Error:', error);
      
      localStorage.removeItem('authToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('email');
      
      setAuth(null, null, null);
      
      if (error.response?.data?.message === 'Email already in use') {
        toast.error(isTamil ? 'இந்த மின்னஞ்சல் ஏற்கனவே பதிவு செய்யப்பட்டுள்ளது' : 'This email is already registered');
      } else if (error.response?.status === 401) {
        toast.error(isTamil ? 'தவறான மின்னஞ்சல் அல்லது கடவுச்சொல்' : 'Invalid email or password');
      } else {
        toast.error(isTamil ? 'பிழை ஏற்பட்டது. மீண்டும் முயற்சிக்கவும்.' : 'An error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8 pt-36">
        <div className="max-w-4xl w-full space-y-8 bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="flex">
            {/* Scout Image */}
            <div className="hidden lg:block w-1/2 bg-cover bg-center" 
                 style={{ backgroundImage: "url('/Images/loginimage.png')" }}>
            </div>
    
            {/* Form Section */}
            <div className="w-full lg:w-1/2 p-8 sm:p-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div>
                  <h2 className="text-3xl font-extrabold text-gray-900">
                    {isSignup ? translations.createAccount[isTamil ? 'ta' : 'en'] : translations.welcomeBack[isTamil ? 'ta' : 'en']}
                  </h2>
                  <p className="mt-2 text-sm text-gray-600">
                    {isSignup ? translations.joinCommunity[isTamil ? 'ta' : 'en'] : translations.signInPrompt[isTamil ? 'ta' : 'en']}
                  </p>
                </div>
    
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-600 text-sm"
                    role="alert"
                  >
                    {error}
                  </motion.div>
                )}
    
                {success && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 p-3 rounded-md bg-green-50 border border-green-200 text-green-600 text-sm"
                    role="alert"
                  >
                    {success}
                  </motion.div>
                )}
    
                <motion.form 
                  className="mt-8 space-y-6" 
                  onSubmit={handleSubmit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="space-y-4">
                    {/* Signup Fields */}
                    <AnimatePresence mode="wait">
                      {isSignup && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                              {translations.username[isTamil ? 'ta' : 'en']}
                            </label>
                            <input
                              id="username"
                              name="username"
                              type="text"
                              required
                              className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                              placeholder={translations.enterUsername[isTamil ? 'ta' : 'en']}
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
    
                    {/* Common Fields */}
                    <div>
                      <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                        {translations.emailAddress[isTamil ? 'ta' : 'en']}
                      </label>
                      <input
                        id="email-address"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                        placeholder={translations.enterEmail[isTamil ? 'ta' : 'en']}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
    
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                        {translations.password[isTamil ? 'ta' : 'en']}
                      </label>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                        placeholder={translations.enterPassword[isTamil ? 'ta' : 'en']}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                  </div>
    
                  {/* Remember Me and Forgot Password */}
                  <AnimatePresence mode="wait">
                    {!isSignup && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <input
                            id="remember-me"
                            name="remember-me"
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                          />
                          <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                            {translations.rememberMe[isTamil ? 'ta' : 'en']}
                          </label>
                        </div>
    
                        <div className="text-sm">
                          <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                            {translations.forgotPassword[isTamil ? 'ta' : 'en']}
                          </a>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
    
                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <div className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {translations.processing[isTamil ? 'ta' : 'en']}
                        </div>
                      ) : (
                        isSignup ? translations.signUp[isTamil ? 'ta' : 'en'] : translations.signIn[isTamil ? 'ta' : 'en']
                      )}
                    </button>
                  </div>
                </motion.form>
    
                {/* Switch between Login/Signup */}
                <motion.div 
                  className="mt-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <p className="text-center text-sm text-gray-600">
                    {isSignup ? translations.alreadyHaveAccount[isTamil ? 'ta' : 'en'] : translations.dontHaveAccount[isTamil ? 'ta' : 'en']}{' '}
                    <button
                      onClick={() => {
                        setIsSignup(!isSignup);
                        setError('');
                        setSuccess('');
                      }}
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      {isSignup ? translations.signIn[isTamil ? 'ta' : 'en'] : translations.signUp[isTamil ? 'ta' : 'en']}
                    </button>
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}